'use client';
// ═══════════════════════════════════════════════════════════════════════════
// « COMBIEN ÇA COÛTE POUR MA FIDUCIAIRE ? » — l'outil, sur la page qui reçoit
// déjà les visites.
// ───────────────────────────────────────────────────────────────────────────
// 📏 POURQUOI ICI ET PAS SUR LA LANDING. Mesuré le 02/09/2026 :
//   /blog/logiciel-paie-fiduciaire-multi-clients .. 1 542 affichages · 0 clic
//   /landing/fiduciaires (la page qui VEND) .......     2 affichages · 0 clic
// L'audience est sur l'article. Poser l'outil sur la landing, c'est l'offrir à
// deux personnes par trimestre.
//
// 🔑 CE QU'IL APPORTE QUE LES CONCURRENTS N'ONT PAS. Relevé du 24/08 (docs
// 30.30) : « AUCUN prestataire belge ne publie de prix à l'acte. » La requête
// « combien coûte un logiciel de gestion fiduciaire » fait 389 affichages et
// reste sans réponse sur tout le marché. Un concurrent qui voudrait répondre
// devrait publier des prix qu'il refuse de publier.
//
// ⚠️ AUCUN PRIX DE CONCURRENT N'EST AFFICHÉ ICI. Le même relevé dit que les
// fourchettes trouvées sont des ordres de grandeur NON attribués : écrire
// « chez X c'est 50 € » serait inventer un chiffre sur un tiers.
//
// ⚠️ LES DEUX FRAIS SE DISENT AVANT, pas après. Un frais découvert en cours de
// route a coûté le dossier Solinas le 28/07/2026 — la mise en route et la durée
// du contrat sont donc affichées dans le résultat, pas en note de bas de page.
// ═══════════════════════════════════════════════════════════════════════════
import { useState } from 'react';
import { coutFiduciaire } from '@/app/lib/cout-fiduciaire';
import { PHRASE_ENGAGEMENT, PHRASE_MISE_EN_ROUTE, euros } from '@/app/lib/conditions-commerciales';

const T = {
  fr: {
    titre: 'Combien ça coûte pour votre fiduciaire ?',
    intro: 'Entrez vos chiffres. Le calcul utilise notre tarif public, celui du contrat.',
    fiches: 'Fiches de paie par mois, tous dossiers confondus',
    dossiers: 'Nombre de dossiers clients (facultatif)',
    facture: 'Ce que vous refacturez par fiche (facultatif)',
    parMois: 'Votre coût par mois',
    an1: 'Première année, mise en route comprise',
    anSuiv: 'Années suivantes',
    parDossier: 'Par dossier client, par mois',
    marge: 'Ce qu’il vous reste, par mois',
    margeAn: 'soit par an',
    perte: '⚠️ À ce tarif de refacturation, vous perdez de l’argent sur chaque fiche.',
    vide: 'Indiquez votre volume mensuel pour voir le calcul.',
    cta: 'Voir l’offre fiduciaires →',
  },
  nl: {
    titre: 'Wat kost het voor uw kantoor?',
    intro: 'Vul uw cijfers in. De berekening gebruikt ons publieke tarief, dat van het contract.',
    fiches: 'Loonfiches per maand, alle dossiers samen',
    dossiers: 'Aantal klantendossiers (optioneel)',
    facture: 'Wat u per loonfiche doorrekent (optioneel)',
    parMois: 'Uw kost per maand',
    an1: 'Eerste jaar, opstartkosten inbegrepen',
    anSuiv: 'Volgende jaren',
    parDossier: 'Per klantendossier, per maand',
    marge: 'Wat u overhoudt, per maand',
    margeAn: 'oftewel per jaar',
    perte: '⚠️ Aan dat doorrekentarief verliest u geld op elke loonfiche.',
    vide: 'Geef uw maandvolume in om de berekening te zien.',
    cta: 'Bekijk het aanbod voor kantoren →',
  },
};

const CADRE = { border: '1px solid #c6a34e55', borderRadius: 12, padding: '22px 24px', background: '#c6a34e0d', margin: '32px 0' };
const CHAMP = { width: '100%', padding: '10px 12px', fontSize: 16, border: '1px solid #d8d2c0', borderRadius: 8, background: '#fff', color: '#1a1a18' };
const LABEL = { display: 'block', fontSize: 13.5, color: '#6a6558', marginBottom: 6 };
const LIGNE = { display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', padding: '9px 0', borderBottom: '1px solid #e8e1cd', gap: 16 };

export default function CoutFiduciaire({ lang = 'fr' }) {
  const t = T[lang] || T.fr;
  const [fiches, setFiches] = useState('');
  const [dossiers, setDossiers] = useState('');
  const [facture, setFacture] = useState('');

  const r = coutFiduciaire({ fichesParMois: fiches, dossiers, factureParFiche: facture });

  return (
    <section style={CADRE}>
      <h2 style={{ fontSize: 20, fontWeight: 800, margin: '0 0 6px', color: '#1a1a18' }}>{t.titre}</h2>
      <p style={{ fontSize: 14.5, color: '#6a6558', margin: '0 0 18px', lineHeight: 1.6 }}>{t.intro}</p>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))', gap: 14, marginBottom: 20 }}>
        <div>
          <label style={LABEL} htmlFor="cf-fiches">{t.fiches}</label>
          <input id="cf-fiches" style={CHAMP} type="number" min="0" inputMode="numeric"
                 value={fiches} onChange={(e) => setFiches(e.target.value)} placeholder="150" />
        </div>
        <div>
          <label style={LABEL} htmlFor="cf-dossiers">{t.dossiers}</label>
          <input id="cf-dossiers" style={CHAMP} type="number" min="0" inputMode="numeric"
                 value={dossiers} onChange={(e) => setDossiers(e.target.value)} placeholder="30" />
        </div>
        <div>
          <label style={LABEL} htmlFor="cf-facture">{t.facture}</label>
          <input id="cf-facture" style={CHAMP} type="number" min="0" inputMode="numeric"
                 value={facture} onChange={(e) => setFacture(e.target.value)} placeholder="55" />
        </div>
      </div>

      {!r && <p style={{ fontSize: 14.5, color: '#8a8578', margin: 0 }}>{t.vide}</p>}

      {r && (
        <div>
          <div style={LIGNE}>
            <span style={{ fontSize: 15, color: '#333330' }}>{t.parMois}</span>
            <strong style={{ fontSize: 22, color: '#1a1a18' }}>{euros(r.coutMois)}</strong>
          </div>
          {r.parDossier !== null && (
            <div style={LIGNE}>
              <span style={{ fontSize: 15, color: '#333330' }}>{t.parDossier}</span>
              <strong style={{ fontSize: 16, color: '#333330' }}>{euros(Math.round(r.parDossier * 100) / 100)}</strong>
            </div>
          )}
          <div style={LIGNE}>
            <span style={{ fontSize: 15, color: '#333330' }}>{t.an1}</span>
            <strong style={{ fontSize: 16, color: '#333330' }}>{euros(r.coutPremiereAnnee)}</strong>
          </div>
          <div style={LIGNE}>
            <span style={{ fontSize: 15, color: '#333330' }}>{t.anSuiv}</span>
            <strong style={{ fontSize: 16, color: '#333330' }}>{euros(r.coutAnneesSuivantes)}</strong>
          </div>

          {r.margeMois !== null && (
            <div style={{ ...LIGNE, borderBottom: 'none', marginTop: 6 }}>
              <span style={{ fontSize: 15, color: '#333330' }}>{t.marge}</span>
              <strong style={{ fontSize: 22, color: r.perte ? '#b91c1c' : '#15803d' }}>
                {euros(r.margeMois)} <span style={{ fontSize: 13, fontWeight: 400, color: '#6a6558' }}>· {t.margeAn} {euros(r.margeAn)}</span>
              </strong>
            </div>
          )}
          {r.perte && (
            <p style={{ fontSize: 14.5, color: '#b91c1c', margin: '4px 0 0', lineHeight: 1.6 }}>{t.perte}</p>
          )}

          {/* Les deux conditions, dans le résultat et pas en note de bas de page. */}
          <p style={{ fontSize: 13, color: '#6a6558', margin: '16px 0 0', lineHeight: 1.65 }}>
            {PHRASE_MISE_EN_ROUTE} {PHRASE_ENGAGEMENT}
          </p>

          <p style={{ margin: '16px 0 0' }}>
            <a href="/landing/fiduciaires" style={{ color: '#c6a34e', fontWeight: 700, textDecoration: 'none', fontSize: 16 }}>{t.cta}</a>
          </p>
        </div>
      )}
    </section>
  );
}
